import { DeleteOutlined, MoreOutlined, WarningFilled } from '@ant-design/icons';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useRequest } from 'ahooks';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import dayjs from 'dayjs';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiPatientDocumentsManager, { PatientDocument } from 'services/api/ApiPatientDocumentsManager';
import { toast } from 'sonner';

export type PatientDocumentsListProps = {
  documents: PatientDocument[];
  isCaregiver?: boolean;
  currentCaregiver?: { id: string; display_name: string } | null;
  onRefresh?: () => void;
};

export default function PatientDocumentsList({
  documents,
  isCaregiver = false,
  currentCaregiver,
  onRefresh,
}: PatientDocumentsListProps) {
  const { t } = useTranslation();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<PatientDocument | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<PatientDocument | null>(null);

  const isSharedWithCurrentCaregiver = (document: PatientDocument): boolean => {
    if (!currentCaregiver) return false;
    return document.shared_with_caregivers.includes(currentCaregiver.id);
  };

  // Fetch and open document
  const { loading: fetchingDocument, run: fetchAndOpenDocument } = useRequest(
    (documentId: string) =>
      isCaregiver
        ? ApiPatientDocumentsManager.retrieveForCaregiver(documentId)
        : ApiPatientDocumentsManager.retrieve(documentId),
    {
      manual: true,
      onSuccess: async (document: PatientDocument) => {
        if (!document.file_url) {
          toast.error(t('documents.errors.loadError'));
          return;
        }
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
          await Browser.open({ url: document.file_url });
        } else {
          window.open(document.file_url, '_blank');
        }
      },
      onError: () => {
        toast.error(t('documents.errors.loadError'));
      },
    },
  );

  // Share document
  const { loading: sharing, run: shareDocument } = useRequest(
    ({ documentId, caregiverId }: { documentId: string; caregiverId: string }) =>
      ApiPatientDocumentsManager.share({ documentId, caregiverId }),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('documents.shareSuccess'));
        onRefresh?.();
      },
      onError: () => {
        toast.error(t('documents.errors.shareError'));
      },
    },
  );

  // Unshare document
  const { loading: unsharing, run: unshareDocument } = useRequest(
    ({ documentId, caregiverId }: { documentId: string; caregiverId: string }) =>
      ApiPatientDocumentsManager.unshare({ documentId, caregiverId }),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('documents.unshareSuccess'));
        onRefresh?.();
      },
      onError: () => {
        toast.error(t('documents.errors.unshareError'));
      },
    },
  );

  // Delete document
  const { loading: deleting, run: deleteDocument } = useRequest(
    (documentId: string) => ApiPatientDocumentsManager.delete(documentId),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('documents.deleteSuccess'));
        onRefresh?.();
      },
      onError: () => {
        toast.error(t('documents.errors.deleteError'));
      },
    },
  );

  // Update document
  const { loading: updating, run: updateDocument } = useRequest(
    ({ documentId, filename }: { documentId: string; filename: string }) =>
      ApiPatientDocumentsManager.update({ documentId, filename }),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('documents.updateSuccess'));
        setEditModalOpen(false);
        setEditingDocument(null);
        setEditDisplayName('');
        onRefresh?.();
      },
      onError: () => {
        toast.error(t('documents.errors.updateError'));
      },
    },
  );

  const loading = sharing || unsharing || deleting || updating || fetchingDocument;

  // Handler functions
  const handleView = (document: PatientDocument) => {
    fetchAndOpenDocument(document.id);
    posthog.capture('patient_documents.document_viewed');
  };

  const handleEdit = (document: PatientDocument) => {
    setEditingDocument(document);
    setEditDisplayName(document.filename);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingDocument(null);
    setEditDisplayName('');
  };

  const handleEditSave = () => {
    if (!editingDocument) return;
    const trimmedName = editDisplayName.trim();
    if (!trimmedName) {
      toast.error(t('documents.errors.emptyName'));
      return;
    }
    updateDocument({ documentId: editingDocument.id, filename: trimmedName });
    posthog.capture('patient_documents.document_renamed');
  };

  const handleShare = (document: PatientDocument) => {
    if (!currentCaregiver) {
      toast.warning(t('documents.errors.noCaregiver'));
      return;
    }
    shareDocument({ documentId: document.id, caregiverId: currentCaregiver.id });
    posthog.capture('patient_documents.document_shared');
  };

  const handleUnshare = (document: PatientDocument) => {
    if (!currentCaregiver) {
      return;
    }
    unshareDocument({ documentId: document.id, caregiverId: currentCaregiver.id });
    posthog.capture('patient_documents.document_unshared');
  };

  const handleDelete = (document: PatientDocument) => {
    setDocumentToDelete(document);
    posthog.capture('patient_documents.document_deleted');
  };

  const confirmDelete = () => {
    if (documentToDelete) {
      deleteDocument(documentToDelete.id);
    }
    setDocumentToDelete(null);
  };

  const renderDocumentItem = (document: PatientDocument) => {
    const isShared = isSharedWithCurrentCaregiver(document);
    const menuItems: { key: string; label: string; danger?: boolean; onClick: () => void }[] = [
      {
        key: 'view',
        label: t('documents.view'),
        onClick: () => handleView(document),
      },
      // Only show edit if not caregiver
      ...(!isCaregiver
        ? [
            {
              key: 'edit',
              label: t('documents.edit'),
              onClick: () => handleEdit(document),
            },
          ]
        : []),
      // Share/unshare options (only for patients, not caregivers)
      ...(!isCaregiver && currentCaregiver
        ? !isShared
          ? [
              {
                key: 'share',
                label: t('documents.shareWith', {
                  caregiverName: currentCaregiver.display_name,
                }),
                onClick: () => handleShare(document),
              },
            ]
          : [
              {
                key: 'unshare',
                label: t('documents.unshareWith', {
                  caregiverName: currentCaregiver.display_name,
                }),
                onClick: () => handleUnshare(document),
              },
            ]
        : []),
      // Only show delete if not caregiver
      ...(!isCaregiver
        ? [
            {
              key: 'delete',
              label: t('common.delete'),
              danger: true,
              onClick: () => handleDelete(document),
            },
          ]
        : []),
    ];

    const metaText = `${dayjs(document.uploaded_at).format('DD/MM/YYYY HH:mm')}`;

    return (
      <li key={document.id} className="mb-2 flex items-center justify-between rounded-md bg-card p-3">
        <div className="mr-4 min-w-0 flex-1 overflow-hidden">
          <div
            className="mb-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium"
            onClick={() => handleView(document)}
          >
            {document.filename}
          </div>
          <div className="text-xs text-subtle-foreground">{metaText}</div>
        </div>
        {menuItems.length > 1 && (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" loading={loading}>
                  <MoreOutlined />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuItems.map(item => (
                  <DropdownMenuItem
                    key={item.key}
                    variant={item.danger ? 'destructive' : 'default'}
                    onSelect={() => item.onClick()}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      <ul className="m-0 list-none p-0">{documents.map(renderDocumentItem)}</ul>
      <Dialog
        open={editModalOpen}
        onOpenChange={nextOpen => {
          if (!nextOpen) closeEditModal();
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('documents.editName')}</DialogTitle>
          </DialogHeader>
          <Input
            value={editDisplayName}
            onChange={e => setEditDisplayName(e.target.value)}
            placeholder={t('documents.enterName')}
            maxLength={255}
            onKeyDown={e => {
              if (e.key === 'Enter') handleEditSave();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditSave} loading={updating}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={documentToDelete !== null}
        onOpenChange={nextOpen => {
          if (!nextOpen) setDocumentToDelete(null);
        }}
      >
        <DialogContent data-testid="confirm-dialog" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="sr-only">{t('common.delete')}</DialogTitle>
            <DialogDescription className="flex items-start gap-3">
              <WarningFilled className="mt-0.5 text-xl text-chart-severity-bad" />
              <span>{t('documents.confirmDelete', { fileName: documentToDelete?.filename ?? '' })}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentToDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <DeleteOutlined />
              {t('documents.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
